import React, { useMemo, useState } from "react";
import "./QuoteForm.css";
import { HiChevronDown } from "react-icons/hi";
import uploadIcon from "../../../assets/icons/uploadcloud.png";
import quoteSideImage from "../../../assets/images/quote/quoteimg.png";
import { submitQuoteRequest } from "../../../api/quoteRequestsApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import PhoneCountrySelect from "../../shared/PhoneCountrySelect/PhoneCountrySelect";
import "../../shared/PhoneCountrySelect/PhoneCountrySelect.css";
import {
  DEFAULT_PHONE_COUNTRY,
  formatPhoneWithDialCode,
  getCountryByCode,
} from "../../../data/countryPhonePrefixes";

const SERVICE_OPTIONS = [
  { value: "", label: "Select service you need" },
  { value: "discovery", label: "Discovery & strategy" },
  { value: "development", label: "Custom development" },
  { value: "design", label: "Product & UX design" },
  { value: "data", label: "Data & analytics" },
  { value: "infra", label: "Cloud & infrastructure" },
  { value: "other", label: "Other" },
];

const BUDGET_OPTIONS = [
  { value: "", label: "Select your budget" },
  { value: "under-10", label: "Under $10,000" },
  { value: "10-50", label: "$10,000 – $50,000" },
  { value: "50-150", label: "$50,000 – $150,000" },
  { value: "150-plus", label: "$150,000+" },
  { value: "unsure", label: "Not sure yet" },
];

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  budget: "",
  description: "",
  privacy: false,
};

const MAX_QUOTE_FILES = 5;

function mergeQuoteFiles(existing, incoming) {
  const merged = [...existing];

  for (const file of incoming) {
    if (merged.length >= MAX_QUOTE_FILES) break;

    const isDuplicate = merged.some(
      (item) =>
        item.name === file.name &&
        item.size === file.size &&
        item.lastModified === file.lastModified
    );
    if (isDuplicate) continue;

    merged.push(file);
  }

  return merged;
}

const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const selectedCountry = useMemo(
    () => getCountryByCode(phoneCountry),
    [phoneCountry]
  );

  const phonePlaceholder = selectedCountry
    ? `${selectedCountry.dial} (555) 000-0000`
    : "(555) 000-0000";

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess(false);
  }

  function handleFilesSelected(fileList) {
    const incoming = Array.from(fileList ?? []);
    if (!incoming.length) return;

    setFiles((prev) => {
      const next = mergeQuoteFiles(prev, incoming);
      if (next.length >= MAX_QUOTE_FILES && prev.length + incoming.length > MAX_QUOTE_FILES) {
        setError(`You can attach up to ${MAX_QUOTE_FILES} files.`);
      } else {
        setError("");
      }
      return next;
    });
    setSuccess(false);
  }

  function removeFile(index) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setError("");
    setSuccess(false);
  }

  function clearFileInput() {
    const fileInput = document.getElementById("qf-files");
    if (fileInput) fileInput.value = "";
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }

    if (!form.email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    if (!form.service) {
      setError("Please select a service of interest.");
      return;
    }

    if (!form.budget) {
      setError("Please select your budget.");
      return;
    }

    if (!form.privacy) {
      setError("Please agree to the privacy policy.");
      return;
    }

    if (files.length > MAX_QUOTE_FILES) {
      setError(`You can attach up to ${MAX_QUOTE_FILES} files.`);
      return;
    }

    const payload = new FormData();
    payload.append("firstName", form.firstName.trim());
    payload.append("lastName", form.lastName.trim());
    payload.append("email", form.email.trim());
    payload.append("phone", formatPhoneWithDialCode(phoneCountry, form.phone));
    payload.append("company", form.company.trim());
    payload.append("service", form.service);
    payload.append("budget", form.budget);
    payload.append("description", form.description.trim());
    payload.append("agreedToPrivacy", "true");
    files.forEach((file) => payload.append("files", file));

    setBusy(true);
    try {
      await submitQuoteRequest(payload);
      setSuccess(true);
      setForm(EMPTY_FORM);
      setPhoneCountry(DEFAULT_PHONE_COUNTRY);
      setFiles([]);
      clearFileInput();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="quote-form">
      <div className="quote-form__inner">
        <div className="quote-form__main">
          <h1 className="quote-form__title">Request a quote</h1>
          <p className="quote-form__subtitle">
            Our friendly team would love to hear from you.
          </p>

          <form className="quote-form__form" onSubmit={handleSubmit} noValidate>
            {success ? (
              <p className="quote-form__status quote-form__status--success" role="status">
                Thank you! Your quote request has been submitted successfully.
              </p>
            ) : null}

            {error ? (
              <p className="quote-form__status quote-form__status--error" role="alert">
                {error}
              </p>
            ) : null}

            <div className="quote-form__row">
              <div className="quote-form__field">
                <label htmlFor="qf-first-name">First name</label>
                <input
                  id="qf-first-name"
                  name="firstName"
                  type="text"
                  placeholder="First name"
                  autoComplete="given-name"
                  value={form.firstName}
                  onChange={(e) => setField("firstName", e.target.value)}
                  disabled={busy}
                  required
                />
              </div>
              <div className="quote-form__field">
                <label htmlFor="qf-last-name">Last name</label>
                <input
                  id="qf-last-name"
                  name="lastName"
                  type="text"
                  placeholder="Last name"
                  autoComplete="family-name"
                  value={form.lastName}
                  onChange={(e) => setField("lastName", e.target.value)}
                  disabled={busy}
                  required
                />
              </div>
            </div>

            <div className="quote-form__field">
              <label htmlFor="qf-phone">Phone number</label>
              <div className="quote-form__phone">
                <PhoneCountrySelect
                  value={phoneCountry}
                  disabled={busy}
                  theme="quote"
                  id="qf-phone-country"
                  onChange={(code) => {
                    setPhoneCountry(code);
                    setError("");
                    setSuccess(false);
                  }}
                />
                <input
                  id="qf-phone"
                  name="phone"
                  type="tel"
                  className="quote-form__phone-input"
                  placeholder={phonePlaceholder}
                  autoComplete="tel-national"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  disabled={busy}
                />
              </div>
            </div>

            <div className="quote-form__field">
              <label htmlFor="qf-email">Email</label>
              <input
                id="qf-email"
                name="email"
                type="email"
                placeholder="you@mail.com"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                disabled={busy}
                required
              />
            </div>

            <div className="quote-form__field">
              <label htmlFor="qf-company">Company name</label>
              <input
                id="qf-company"
                name="company"
                type="text"
                placeholder="Your company name"
                autoComplete="organization"
                value={form.company}
                onChange={(e) => setField("company", e.target.value)}
                disabled={busy}
              />
            </div>

            <div className="quote-form__field">
              <label htmlFor="qf-service">Service of interest</label>
              <div className="quote-form__select-wrap">
                <select
                  id="qf-service"
                  name="service"
                  className="quote-form__select"
                  value={form.service}
                  onChange={(e) => setField("service", e.target.value)}
                  disabled={busy}
                  required
                >
                  {SERVICE_OPTIONS.map((opt) => (
                    <option
                      key={opt.value || "placeholder"}
                      value={opt.value}
                      disabled={opt.value === ""}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
                <HiChevronDown
                  className="quote-form__select-chevron"
                  aria-hidden
                />
              </div>
            </div>

            <div className="quote-form__field">
              <label htmlFor="qf-budget">Budget</label>
              <div className="quote-form__select-wrap">
                <select
                  id="qf-budget"
                  name="budget"
                  className="quote-form__select"
                  value={form.budget}
                  onChange={(e) => setField("budget", e.target.value)}
                  disabled={busy}
                  required
                >
                  {BUDGET_OPTIONS.map((opt) => (
                    <option
                      key={opt.value || "placeholder-budget"}
                      value={opt.value}
                      disabled={opt.value === ""}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
                <HiChevronDown
                  className="quote-form__select-chevron"
                  aria-hidden
                />
              </div>
            </div>

            <div className="quote-form__field">
              <label htmlFor="qf-description">Project description</label>
              <textarea
                id="qf-description"
                name="description"
                rows={6}
                placeholder="Tell us about your project..."
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                disabled={busy}
              />
            </div>

            <div className="quote-form__field">
              <span className="quote-form__field-label" id="qf-files-label">
                Attach files
              </span>
              <label
                className="quote-form__upload"
                htmlFor="qf-files"
                onDragOver={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!busy) handleFilesSelected(event.dataTransfer.files);
                }}
              >
                <input
                  id="qf-files"
                  name="files"
                  type="file"
                  multiple
                  className="quote-form__upload-input"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.gif"
                  onChange={(event) => {
                    handleFilesSelected(event.target.files);
                    clearFileInput();
                  }}
                  disabled={busy || files.length >= MAX_QUOTE_FILES}
                />
                <span className="quote-form__upload-icon" aria-hidden="true">
                  <img src={uploadIcon} alt="" />
                </span>
                <p className="quote-form__upload-text">
                  <span className="quote-form__upload-cta">Click to upload</span>{" "}
                  <span className="quote-form__upload-muted">
                    or drag and drop
                  </span>
                </p>
                <small className="quote-form__upload-hint">
                  {files.length
                    ? `${files.length} of ${MAX_QUOTE_FILES} files selected`
                    : `Add up to ${MAX_QUOTE_FILES} documents or images.`}
                </small>
              </label>

              {files.length > 0 ? (
                <ul className="quote-form__file-list" aria-label="Selected files">
                  {files.map((file, index) => (
                    <li key={`${file.name}-${file.size}-${file.lastModified}`}>
                      <span className="quote-form__file-name" title={file.name}>
                        {file.name}
                      </span>
                      <button
                        type="button"
                        className="quote-form__file-remove"
                        onClick={() => removeFile(index)}
                        disabled={busy}
                        aria-label={`Remove ${file.name}`}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div className="quote-form__check">
              <input
                id="qf-policy"
                name="policy"
                type="checkbox"
                checked={form.privacy}
                onChange={(e) => setField("privacy", e.target.checked)}
                disabled={busy}
                required
              />
              <label htmlFor="qf-policy">
                You agree to our friendly <u>privacy policy</u>.
              </label>
            </div>

            <button
              type="submit"
              className="buttonclip quote-form__submit"
              disabled={busy}
            >
              {busy ? "Submitting…" : "Request a quote"}
            </button>
          </form>
        </div>

        <div className="quote-form__visual" aria-hidden="true">
          <img
            src={quoteSideImage}
            alt=""
            className="quote-form__visual-img"
          />
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
